/* ═══════════════════════════════════════════════════════════════
   Module CATEGORIES — a nested tree over the firm-wide module library
   (migration 056), plus free-text TAGS on `SpecModule` (spec-modules.ts).

   The owner's use case: a large equipment index — module "Q-SYS AD-S12"
   filed under Equipment › Division 27 › Speakers › QSC, tagged
   "12" speaker", "flown". One category per module (nullable — uncategorized
   is a valid state); many tags per module.

   Pure, UI-free. The React side (not built here) reads categories/modules
   through `src/renderer/services/categoriesService.ts` + `moduleService.ts`
   and feeds the result into these functions — nothing here does I/O.

   ⛔ Mirrors `ec2-api/module-categories.js` BY RULE, not by import (same
   precedent as every other client/server pure-logic pair in this repo that
   can't share a bundle target). Same thresholds, same tag-normalization
   rule. If either side's numbers change, change both.
   ═══════════════════════════════════════════════════════════════ */

import type { SpecModule } from './spec-modules';

// ─── Types ───

/** One row of `module_categories` — a node in the tree. */
export interface ModuleCategory {
  id: string;
  firmId?: string;
  /** NULL/absent = a root category. */
  parentId?: string | null;
  name: string;
  sortOrder?: number;
  createdBy?: string;
  createdAt?: string;
  modifiedBy?: string;
  lastModified?: string;
}

/** A tree node: the category plus its already-built children, in
 *  `sortOrder` (then name) order. */
export interface CategoryTreeNode extends ModuleCategory {
  children: CategoryTreeNode[];
}

// ─── Same bounds as module-categories.js (server) — keep in sync ───

export const MAX_CATEGORY_NAME_LEN = 80;
export const MAX_CATEGORY_DEPTH = 8;
export const MAX_MODULE_TAGS = 30;
export const MAX_TAG_LEN = 40;

/** Mirrors `ec2-api/module-categories.js` `validateCategoryName` — trim +
 *  length-bound + forbid the path separators `parseCategoryPath` splits a
 *  typed path on (`>` and `›`; `/` is a legitimate character, e.g. "A/V").
 *  The server is the one that can actually refuse a save; this lets a
 *  picker show the SAME message before it ever round-trips. */
export function validateCategoryName(
  raw: string | null | undefined
): { ok: true; name: string } | { ok: false; reason: string } {
  const name = typeof raw === 'string' ? raw.trim() : '';
  if (!name) return { ok: false, reason: 'Category name is required' };
  if (name.length > MAX_CATEGORY_NAME_LEN) {
    return {
      ok: false,
      reason: `Category name must be ${MAX_CATEGORY_NAME_LEN} characters or fewer`,
    };
  }
  if (/[>›]/.test(name)) {
    return { ok: false, reason: "Category names can't contain > or ›" };
  }
  return { ok: true, name };
}

// ─── Tree building ───

const bySortThenName = (a: ModuleCategory, b: ModuleCategory): number => {
  const so = (a.sortOrder || 0) - (b.sortOrder || 0);
  if (so !== 0) return so;
  return (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
};

/** Is `id` part of a cycle in its OWN ancestor chain (walking `parentOf` up
 *  from `id` eventually comes back to `id` itself, rather than reaching
 *  `null`)? A bounded walk — each step either reaches `id` (true), reaches
 *  a DIFFERENT node already seen on this walk (a cycle that doesn't
 *  include `id` — false, not our problem to fix here), or runs out of
 *  rungs (false). Never loops forever. */
function isCategoryCycleMember(id: string, parentOf: Map<string, string | null>): boolean {
  let cur = parentOf.has(id) ? (parentOf.get(id) ?? null) : null;
  const seen = new Set<string>([id]);
  while (cur != null) {
    if (cur === id) return true;
    if (seen.has(cur)) return false;
    seen.add(cur);
    cur = parentOf.has(cur) ? (parentOf.get(cur) ?? null) : null;
  }
  return false;
}

/** The firm's flat category rows → a tree, root categories first. A row
 *  whose `parentId` names nothing in the list (dangling — the parent was
 *  deleted some other way) is treated as a ROOT rather than dropped, so a
 *  category can never silently vanish from the UI. Corrupt data that forms
 *  an actual PARENT LOOP (should never happen — the server refuses the
 *  write that would create one — but a stale cache or a bug elsewhere could
 *  still hand this function one) gets the same treatment: every category in
 *  the loop renders as its OWN top-level root rather than the whole loop
 *  silently disappearing from the tree. */
export function buildCategoryTree(categories: ModuleCategory[]): CategoryTreeNode[] {
  const list = categories || [];
  const ids = new Set(list.map((c) => c.id));
  const parentOf = new Map<string, string | null>(
    list.map((c) => [c.id, c.parentId && ids.has(c.parentId) ? c.parentId : null])
  );
  const byParent = new Map<string | null, ModuleCategory[]>();
  for (const cat of list) {
    let parentKey = cat.parentId && ids.has(cat.parentId) ? cat.parentId : null;
    if (parentKey != null && isCategoryCycleMember(cat.id, parentOf)) parentKey = null;
    const bucket = byParent.get(parentKey);
    if (bucket) bucket.push(cat);
    else byParent.set(parentKey, [cat]);
  }
  const build = (parentId: string | null): CategoryTreeNode[] =>
    (byParent.get(parentId) || [])
      .slice()
      .sort(bySortThenName)
      .map((cat) => ({ ...cat, children: build(cat.id) }));
  return build(null);
}

// ─── Paths ───

/** Root → leaf ancestor chain ending at `id` itself. Empty array if `id`
 *  names no category (never throws — a dangling/unknown id is "no path"). */
export function categoryPath(
  id: string | null | undefined,
  categories: ModuleCategory[]
): ModuleCategory[] {
  if (!id) return [];
  const byId = new Map((categories || []).map((c) => [c.id, c]));
  const path: ModuleCategory[] = [];
  let cur: string | null | undefined = id;
  const seen = new Set<string>();
  while (cur) {
    const node = byId.get(cur);
    if (!node || seen.has(cur)) break;
    seen.add(cur);
    path.unshift(node);
    cur = node.parentId;
  }
  return path;
}

/** "Equipment › Division 27 › Speakers › QSC". Empty string for no path. */
export function categoryPathLabel(
  id: string | null | undefined,
  categories: ModuleCategory[]
): string {
  return categoryPath(id, categories)
    .map((c) => c.name)
    .join(' › ');
}

/** `id` + every descendant's id (depth-first, order not meaningful) — the
 *  set "this category or anything under it". Visited-set guarded: corrupt
 *  data with a parent LOOP in it (should never happen — see
 *  `buildCategoryTree`'s header) would otherwise walk the loop forever
 *  instead of terminating. */
export function descendantCategoryIds(id: string, categories: ModuleCategory[]): string[] {
  const children = new Map<string, string[]>();
  for (const cat of categories || []) {
    if (!cat.parentId) continue;
    const bucket = children.get(cat.parentId);
    if (bucket) bucket.push(cat.id);
    else children.set(cat.parentId, [cat.id]);
  }
  const out: string[] = [id];
  const visited = new Set<string>([id]);
  const stack = [id];
  while (stack.length) {
    const cur = stack.pop()!;
    for (const childId of children.get(cur) || []) {
      if (visited.has(childId)) continue;
      visited.add(childId);
      out.push(childId);
      stack.push(childId);
    }
  }
  return out;
}

// ─── Module ↔ category ───

/** A module's category, treating a dangling id (the category row is gone)
 *  as uncategorized rather than a crash or a hidden module — the one law
 *  the brief states explicitly. */
export function moduleEffectiveCategoryId(
  module: Pick<SpecModule, 'categoryId'>,
  categories: ModuleCategory[]
): string | null {
  const id = module.categoryId;
  if (!id) return null;
  return categories.some((c) => c.id === id) ? id : null;
}

/** Modules filed under `categoryId` (null = uncategorized — never "all").
 *  `includeDescendants` widens the match to the whole subtree, which is
 *  what a category-page view wants; omit it for "directly in this
 *  category only". A module whose stored category is dangling counts as
 *  uncategorized, per `moduleEffectiveCategoryId`. */
export function modulesInCategory(
  modules: SpecModule[],
  categoryId: string | null,
  categories: ModuleCategory[],
  opts?: { includeDescendants?: boolean }
): SpecModule[] {
  if (categoryId === null) {
    return modules.filter((m) => moduleEffectiveCategoryId(m, categories) === null);
  }
  const wanted = opts?.includeDescendants
    ? new Set(descendantCategoryIds(categoryId, categories))
    : new Set([categoryId]);
  return modules.filter((m) => {
    const eff = moduleEffectiveCategoryId(m, categories);
    return eff !== null && wanted.has(eff);
  });
}

/** categoryId -> live module count, INCLUDING descendants (a parent's
 *  count is never less than the sum its children show). `null` is not a
 *  key here — "uncategorized" has no node to badge. */
export function moduleCountByCategory(
  modules: SpecModule[],
  categories: ModuleCategory[]
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const cat of categories || []) {
    out[cat.id] = modulesInCategory(modules, cat.id, categories, {
      includeDescendants: true,
    }).length;
  }
  return out;
}

// ─── Cycle guard (client-side hint; the server re-checks, binding) ───

/** Would reparenting `movingId` under `newParentId` make `movingId` its own
 *  ancestor? Mirrors ec2-api/module-categories.js `wouldCreateCycle`. */
export function wouldCreateCycle(
  movingId: string,
  newParentId: string | null,
  categories: ModuleCategory[]
): boolean {
  if (!movingId || !newParentId) return false;
  const parentOf = new Map((categories || []).map((c) => [c.id, c.parentId ?? null]));
  let cur: string | null = newParentId;
  const seen = new Set<string>();
  while (cur) {
    if (cur === movingId) return true;
    if (seen.has(cur)) break;
    seen.add(cur);
    cur = parentOf.get(cur) ?? null;
  }
  return false;
}

// ─── Typed path parsing ("Equipment > Division 27 › Speakers") ───

/** Split a human-typed path on `>` or `›` ONLY, trim each rung, drop
 *  empties. `/` is NOT a separator — a category can legitimately be named
 *  "A/V", and splitting on `/` would break it into two rungs that don't
 *  exist. `"Equipment > Division 27 › Speakers"` →
 *  `["Equipment", "Division 27", "Speakers"]`; `"A/V > Cables"` →
 *  `["A/V", "Cables"]`. */
export function parseCategoryPath(input: string): string[] {
  return (input || '')
    .split(/[>›]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/** A typed path → what already exists vs. what a "type to create" flow
 *  still has to create, walking root-to-leaf and matching each rung
 *  case-insensitively against the existing tree at that level. */
export function resolveOrPlanCategoryPath(
  names: string[],
  categories: ModuleCategory[]
): { existingId: string | null; toCreate: string[] } {
  let parentId: string | null = null;
  for (let i = 0; i < names.length; i++) {
    const want = names[i].toLowerCase();
    const match = (categories || []).find(
      (c) => (c.parentId ?? null) === parentId && c.name.trim().toLowerCase() === want
    );
    if (!match) return { existingId: parentId, toCreate: names.slice(i) };
    parentId = match.id;
  }
  return { existingId: parentId, toCreate: [] };
}

// ─── Tags ───

/** Trim, drop empties, de-duplicate case-insensitively (first spelling
 *  wins), cap length and count. Mirrors ec2-api/module-categories.js
 *  `normalizeAndValidateTags` exactly, minus the reject-with-reason path —
 *  this is the CLIENT'S best-effort cleanup for its own optimistic update;
 *  the server is the one that can actually refuse a save. */
export function normalizeTags(input: string[] | null | undefined): string[] {
  const seen = new Map<string, string>();
  for (const raw of input || []) {
    if (typeof raw !== 'string') continue;
    const trimmed = raw.trim();
    if (!trimmed || trimmed.length > MAX_TAG_LEN) continue;
    const key = trimmed.toLowerCase();
    if (!seen.has(key)) seen.set(key, trimmed);
  }
  return [...seen.values()].slice(0, MAX_MODULE_TAGS);
}

/** Defensive READ-side coercion. `module_library.tags` is jsonb and should
 *  always arrive as `string[]`, but a pre-056 row, a migration-drifted
 *  server, or a value that round-tripped through one JSON.stringify too
 *  many could hand the renderer something else — an already-encoded JSON
 *  STRING, `null`/`undefined`, or outright garbage (object, number). A
 *  single bad row must never throw: every module row is pushed through
 *  this (see `moduleService.ts` `listAll`) so every consumer downstream —
 *  `moduleTagFilter.ts`, the Tags input, the Tags rail — can trust `tags`
 *  is ALWAYS a clean array of strings, never guessing at a shape. */
export function toModuleTagsArray(input: unknown): string[] {
  let value: unknown = input;
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value);
    } catch {
      return []; // free text, not JSON — never invent a tag out of it
    }
  }
  if (!Array.isArray(value)) return [];
  return value.filter((t): t is string => typeof t === 'string');
}

/** Result of `validateTags`: the best-effort VALID subset, plus every
 *  problem found — unlike `normalizeTags` above, a caller can actually show
 *  the user what got dropped and why instead of a silent subtraction. */
export interface ValidateTagsResult {
  tags: string[];
  errors: string[];
}

/** Same rules as `normalizeTags`, and identical to ec2-api/module-
 *  categories.js `normalizeAndValidateTags` (≤30 tags, each 1–40 chars
 *  after trim, case-insensitive de-dupe keeping the first spelling) — but
 *  COLLECTS every problem instead of dropping silently or stopping at the
 *  first, so a picker's inline error can tell the user what happened. The
 *  server is still the one write that can actually refuse a save. */
export function validateTags(input: string[] | null | undefined): ValidateTagsResult {
  const errors: string[] = [];
  const seen = new Map<string, string>();
  for (const raw of input || []) {
    if (typeof raw !== 'string') {
      errors.push('Each tag must be text');
      continue;
    }
    const trimmed = raw.trim();
    if (trimmed.length < 1) {
      errors.push("A tag can't be empty");
      continue;
    }
    if (trimmed.length > MAX_TAG_LEN) {
      errors.push(`"${trimmed}" is too long — tags are ${MAX_TAG_LEN} characters or fewer`);
      continue;
    }
    const key = trimmed.toLowerCase();
    if (!seen.has(key)) seen.set(key, trimmed);
  }
  let tags = [...seen.values()];
  if (tags.length > MAX_MODULE_TAGS) {
    errors.push(`A module can carry at most ${MAX_MODULE_TAGS} tags`);
    tags = tags.slice(0, MAX_MODULE_TAGS);
  }
  return { tags, errors };
}

/** Every tag in use across `modules`, most-used first (ties broken
 *  alphabetically so the order is stable/testable). */
export function collectTags(modules: SpecModule[]): Array<{ tag: string; count: number }> {
  const counts = new Map<string, { tag: string; count: number }>();
  for (const m of modules || []) {
    for (const raw of m.tags || []) {
      const key = raw.toLowerCase();
      const existing = counts.get(key);
      if (existing) existing.count++;
      else counts.set(key, { tag: raw, count: 1 });
    }
  }
  return [...counts.values()].sort((a, b) => b.count - a.count || a.tag.localeCompare(b.tag));
}

// ─── Search ───

/** Does `module` match a free-text `query` by name, description, its
 *  category's full path, or any tag? Case-insensitive substring match,
 *  the same "forgiving" search every other library page in this app uses. */
export function moduleMatchesSearch(
  module: SpecModule,
  query: string,
  categories: ModuleCategory[]
): boolean {
  const q = (query || '').trim().toLowerCase();
  if (!q) return true;
  if ((module.name || '').toLowerCase().includes(q)) return true;
  if ((module.description || '').toLowerCase().includes(q)) return true;
  const eff = moduleEffectiveCategoryId(module, categories);
  if (eff && categoryPathLabel(eff, categories).toLowerCase().includes(q)) return true;
  return (module.tags || []).some((t) => t.toLowerCase().includes(q));
}
